/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-10-03
 * @description ResetPassword screen for the user
 */
import { Box, Button, Typography, colors } from "@mui/material";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchData } from "../../../components/hooks/useFetchData";
import { AuthBackground } from "../../../components/common/AuthBackground";
import { LoadingOverlay } from "../../../components/common/LoadingOverlay";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { CrossroadsLogo } from "../../../assets/images/CrossroadsLogo";

const ResetPassword = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [onRequest, setOnRequest] = useState(false);
  const [loginProgress, setLoginProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const { fetchData } = useFetchData();

  const onSignIn = async (data, setError) => {
    const { newpassword } = data;
    const body = {
      passwordresetkey: id,
      password: newpassword,
    };

    const result = await fetchData("resetpassword", {
      method: "post",
      body: body,
    });

    if (result.code === 200) {
      setOnRequest(true);
      const interval = setInterval(() => {
        setLoginProgress((prev) => {
          const newProgress = prev + 100 / 40;
          if (newProgress >= 100) {
            clearInterval(interval);
            setOnRequest(false);
            setSuccess(true);
            setSuccessMsg(result.message);
          }
          return newProgress;
        });
      }, 20);
    } else {
      setError("root", {
        type: "manual",
        message: result?.message || "Invalid credentials",
      });
      return;
    }
  };

  return (
    <Box
      position="relative"
      height="100vh"
      sx={{ "::-webkit-scrollbar": { display: "none" } }}
    >
      <AuthBackground />
      <Box
        sx={{
          position: "relative",
          left: 0,
          height: "100%",
          width: { xl: "30%", lg: "40%", md: "50%", xs: "100%" },
          transition: "all 1s ease-in-out",
          bgcolor: colors.common.white,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            opacity: 1,
            transition: "all 0.3s ease-in-out",
            height: "100%",
            "::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 5,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CrossroadsLogo width="100%" height={100} />
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              "::-webkit-scrollbar": { display: "none" },
            }}
          >
            {success ? (
              <Box textAlign="center">
                <iframe
                  src="https://lottie.host/embed/d6faa36f-3298-4360-a10c-47596bf1bcd4/Y85AD0fyJ0.json"
                  width="200"
                  height="200"
                  frameBorder="0"
                  title="Success Animation"
                />
                {successMsg !== "" ? (
                  <Typography>{successMsg}</Typography>
                ) : (
                  <>
                    <Typography variant="h4" textAlign="center" color="success">
                      Password reset
                    </Typography>
                    <Typography variant="h4" textAlign="center" color="success">
                      successfully
                    </Typography>
                  </>
                )}
                <Button
                  variant="outlined"
                  sx={{ mt: 8 }}
                  onClick={() => navigate("/")}
                >
                  <Typography textTransform="none">Back to login</Typography>
                </Button>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 5,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography component="h1" variant="h5">
                    Reset Password
                  </Typography>
                </Box>
                <ResetPasswordForm onSubmit={onSignIn} />
              </>
            )}
          </Box>
          {onRequest && <LoadingOverlay loginProgress={loginProgress} />}
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;
